import dayjs from 'dayjs';
import { getToday } from './convertDate';
import { TNullable } from '@/types/common';
import { TDateFormat } from '@/types/date';
import { TOrganizationContractServerCamel } from '@/types/organizations';

const CONTRACT_PERIOD_FORMAT = 'MM/DD/YYYY';

/**
 * Sorts contracts by start date, oldest first.
 * @param contracts - The contracts to sort.
 * @returns A sorted copy of the contracts.
 */
const sortByStartDate = (contracts: TOrganizationContractServerCamel[]) =>
  [...contracts].sort((a, b) =>
    dayjs(a.contractStartDate).diff(b.contractStartDate),
  );

/**
 * Checks whether a contract starts on or before the given day. A saved
 * contract that has started is locked: its fields cannot change and it cannot
 * be deleted.
 * @param startDate - The contract's start date; a contract without one has
 * not started.
 * @param day - The day to compare against; today (UTC) by default.
 * @returns `true` if the contract has started by that day.
 */
export const hasContractStarted = (
  startDate?: TNullable<TDateFormat>,
  day: TDateFormat = getToday(),
) => !!startDate && !dayjs(startDate).isAfter(day, 'day');

/**
 * Checks whether a contract's period contains the given day, both ends
 * included.
 * @param contract - The contract to check.
 * @param day - The day to check.
 * @returns `true` if the contract is ongoing on that day.
 */
export const isContractOngoing = (
  contract: TOrganizationContractServerCamel,
  day: TDateFormat,
) =>
  hasContractStarted(contract.contractStartDate, day) &&
  !dayjs(contract.contractEndDate).isBefore(day, 'day');

/**
 * Finds the contract whose period contains today (UTC).
 * @param contracts - The organization's contracts.
 * @returns The ongoing contract, or `undefined` when there is none.
 */
export const getOngoingContract = (
  contracts: TOrganizationContractServerCamel[] | undefined,
): TOrganizationContractServerCamel | undefined => {
  const today = getToday();
  return contracts?.find((contract) => isContractOngoing(contract, today));
};

/**
 * Lists the contracts that have already gone live, newest first.
 * @param contracts - The organization's contracts.
 * @returns The contracts that start on or before today (UTC).
 */
export const getStartedContracts = (
  contracts: TOrganizationContractServerCamel[] | undefined,
): TOrganizationContractServerCamel[] => {
  const today = getToday();
  return sortByStartDate(
    (contracts ?? []).filter((contract) =>
      hasContractStarted(contract.contractStartDate, today),
    ),
  ).reverse();
};

/**
 * Formats a contract's period for display, e.g. `01/01/2026 - 12/31/2026`.
 * @param contract - The contract to format.
 * @returns The contract period.
 */
export const formatContractPeriod = (
  contract: TOrganizationContractServerCamel,
) =>
  [contract.contractStartDate, contract.contractEndDate]
    .map((date) => dayjs(date).format(CONTRACT_PERIOD_FORMAT))
    .join(' - ');
